import React, { useState, useEffect } from 'react';
import { api } from '../../api/api';
import { FormInput } from '../common';
import { ensureAiConfig } from '../../utils/aiConfig';

const QUICK_ACTION_COMMANDS = [
  { value: 'rewrite_full', label: 'Rewrite full' },
  { value: 'improve', label: 'Polish' },
  { value: 'summarize', label: 'Summarise' },
  { value: 'expand', label: 'Expand' },
];

const GlobalSettings = ({ showNotification, onSaved }) => {
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newFontName, setNewFontName] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await api.getGlobalSettings();

        // Ensure proper data structure
        const normalizedData = {
          placeholders: data.placeholders || {},
          imageUrls: data.imageUrls || {},
          aiConfig: ensureAiConfig(data.aiConfig),
          ...data,
        };
        normalizedData.customFonts = Array.isArray(normalizedData.customFonts)
          ? normalizedData.customFonts.filter(
              font => typeof font === 'string' && font.trim()
            )
          : [];

        setGlobalSettings(normalizedData);
      } catch (error) {
        console.error('Error fetching global settings:', error);
        showNotification(
          'Failed to load global settings: ' + error.message,
          'error'
        );
        // Initialize with default structure if fetch fails
        const defaultSettings = {
          placeholders: {},
          imageUrls: {},
          aiConfig: ensureAiConfig(null),
          customFonts: [],
        };
        setGlobalSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [showNotification]);

  const handleInputChange = (category, key, value) => {
    setGlobalSettings(prev => {
      const updated = {
        ...prev,
        [category]: {
          ...(prev[category] || {}),
          [key]: value,
        },
      };
      return updated;
    });
  };

  const updateAiConfig = updater => {
    setGlobalSettings(prev => {
      const current = ensureAiConfig(prev?.aiConfig);
      const next = updater(JSON.parse(JSON.stringify(current)));
      const updated = {
        ...prev,
        aiConfig: next,
      };
      return updated;
    });
  };

  const handleAiCredentialChange = (key, value) => {
    updateAiConfig(aiConfig => {
      aiConfig[key] = value;
      return aiConfig;
    });
  };

  const handleLanguageChange = (index, key, value) => {
    updateAiConfig(aiConfig => {
      aiConfig.languages[index] = {
        ...(aiConfig.languages[index] || {}),
        [key]: value,
      };
      return aiConfig;
    });
  };

  const addLanguage = () => {
    updateAiConfig(aiConfig => {
      aiConfig.languages.push({ id: '', label: '', prompt: '' });
      return aiConfig;
    });
  };

  const removeLanguage = index => {
    updateAiConfig(aiConfig => {
      aiConfig.languages.splice(index, 1);
      if (aiConfig.languages.length === 0) {
        aiConfig.languages = ensureAiConfig(null).languages;
      }
      return aiConfig;
    });
  };

  const handleCategoryFieldChange = (categoryIndex, key, value) => {
    updateAiConfig(aiConfig => {
      const category = aiConfig.snippetCategories[categoryIndex] || {};
      aiConfig.snippetCategories[categoryIndex] = {
        ...category,
        [key]: value,
        options: Array.isArray(category.options) ? category.options : [],
      };
      return aiConfig;
    });
  };

  const handleSnippetChange = (categoryIndex, optionIndex, key, value) => {
    updateAiConfig(aiConfig => {
      const category = aiConfig.snippetCategories[categoryIndex] || { options: [] };
      const options = Array.isArray(category.options) ? category.options : [];
      options[optionIndex] = {
        ...(options[optionIndex] || {}),
        [key]: value,
      };
      category.options = options;
      aiConfig.snippetCategories[categoryIndex] = category;
      return aiConfig;
    });
  };

  const handleQuickActionGroupChange = (groupIndex, key, value) => {
    updateAiConfig(aiConfig => {
      const groups = Array.isArray(aiConfig.textTemplateQuickActions)
        ? aiConfig.textTemplateQuickActions
        : [];
      groups[groupIndex] = {
        ...(groups[groupIndex] || {}),
        [key]: value,
        actions: Array.isArray(groups[groupIndex]?.actions)
          ? groups[groupIndex].actions
          : [],
      };
      aiConfig.textTemplateQuickActions = groups;
      return aiConfig;
    });
  };

  const handleQuickActionChange = (groupIndex, actionIndex, key, value) => {
    updateAiConfig(aiConfig => {
      const groups = Array.isArray(aiConfig.textTemplateQuickActions)
        ? aiConfig.textTemplateQuickActions
        : [];
      const group = groups[groupIndex] || { label: '', actions: [] };
      const actions = Array.isArray(group.actions) ? group.actions : [];
      actions[actionIndex] = {
        ...(actions[actionIndex] || {}),
        [key]: value,
      };
      group.actions = actions;
      groups[groupIndex] = group;
      aiConfig.textTemplateQuickActions = groups;
      return aiConfig;
    });
  };

  const addQuickActionGroup = () => {
    updateAiConfig(aiConfig => {
      const groups = Array.isArray(aiConfig.textTemplateQuickActions)
        ? aiConfig.textTemplateQuickActions
        : [];
      groups.push({
        label: 'New group',
        actions: [],
      });
      aiConfig.textTemplateQuickActions = groups;
      return aiConfig;
    });
  };

  const removeQuickActionGroup = groupIndex => {
    updateAiConfig(aiConfig => {
      const groups = Array.isArray(aiConfig.textTemplateQuickActions)
        ? aiConfig.textTemplateQuickActions
        : [];
      groups.splice(groupIndex, 1);
      aiConfig.textTemplateQuickActions = groups;
      return aiConfig;
    });
  };

  const addQuickAction = groupIndex => {
    updateAiConfig(aiConfig => {
      const groups = Array.isArray(aiConfig.textTemplateQuickActions)
        ? aiConfig.textTemplateQuickActions
        : [];
      const group = groups[groupIndex] || { label: '', actions: [] };
      const actions = Array.isArray(group.actions) ? group.actions : [];
      actions.push({
        label: 'New action',
        command: 'improve',
        prompt: '',
      });
      group.actions = actions;
      groups[groupIndex] = group;
      aiConfig.textTemplateQuickActions = groups;
      return aiConfig;
    });
  };

  const removeQuickAction = (groupIndex, actionIndex) => {
    updateAiConfig(aiConfig => {
      const groups = Array.isArray(aiConfig.textTemplateQuickActions)
        ? aiConfig.textTemplateQuickActions
        : [];
      const group = groups[groupIndex] || { label: '', actions: [] };
      const actions = Array.isArray(group.actions) ? group.actions : [];
      actions.splice(actionIndex, 1);
      group.actions = actions;
      groups[groupIndex] = group;
      aiConfig.textTemplateQuickActions = groups;
      return aiConfig;
    });
  };

  const addSnippetCategory = () => {
    updateAiConfig(aiConfig => {
      aiConfig.snippetCategories.push({
        id: '',
        label: '',
        description: '',
        options: [],
      });
      return aiConfig;
    });
  };

  const removeSnippetCategory = index => {
    updateAiConfig(aiConfig => {
      aiConfig.snippetCategories.splice(index, 1);
      return aiConfig;
    });
  };

  const addSnippetOption = categoryIndex => {
    updateAiConfig(aiConfig => {
      const category = aiConfig.snippetCategories[categoryIndex] || { options: [] };
      if (!Array.isArray(category.options)) {
        category.options = [];
      }
      category.options.push({ id: '', label: '', prompt: '' });
      aiConfig.snippetCategories[categoryIndex] = category;
      return aiConfig;
    });
  };

  const removeSnippetOption = (categoryIndex, optionIndex) => {
    updateAiConfig(aiConfig => {
      const category = aiConfig.snippetCategories[categoryIndex];
      if (category && Array.isArray(category.options)) {
        category.options.splice(optionIndex, 1);
      }
      return aiConfig;
    });
  };

  const handleAddCustomFont = () => {
    const trimmed = (newFontName || '').trim();
    if (!trimmed) {
      showNotification('Enter a font name before adding it.', 'error');
      return;
    }
    setGlobalSettings(prev => {
      const existing = Array.isArray(prev?.customFonts)
        ? prev.customFonts.slice()
        : [];
      if (existing.some(font => font.toLowerCase() === trimmed.toLowerCase())) {
        showNotification('This font is already available.', 'warning');
        return prev;
      }
      const updated = {
        ...prev,
        customFonts: [...existing, trimmed],
      };
      return updated;
    });
    setNewFontName('');
  };

  const handleRemoveCustomFont = fontName => {
    setGlobalSettings(prev => ({
      ...prev,
      customFonts: (prev?.customFonts || []).filter(font => font !== fontName),
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Validate data structure before saving
      const sanitizedFonts = Array.isArray(globalSettings.customFonts)
        ? globalSettings.customFonts.filter(
            font => typeof font === 'string' && font.trim()
          )
        : [];
      const dataToSave = {
        ...globalSettings,
        placeholders: globalSettings.placeholders || {},
        imageUrls: globalSettings.imageUrls || {},
        aiConfig: (() => {
          const next = ensureAiConfig(globalSettings.aiConfig);
          if (typeof next.apiKey === 'string') {
            next.apiKey = next.apiKey.trim();
          }
          return next;
        })(),
        customFonts: sanitizedFonts,
      };

      await api.updateGlobalSettings(dataToSave);
      showNotification('Global settings saved successfully!', 'success');
      if (onSaved) {
        try {
          onSaved(dataToSave);
        } catch (e) {
          console.warn('onSaved error', e);
        }
      }
    } catch (error) {
      console.error('Error saving global settings:', error);
      showNotification(
        'Failed to save global settings: ' + error.message,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const openPlaceholderEditor = () => {
    window.open('/?mode=placeholder_editor', '_blank');
  };

  if (loading) {
    return <p>Loading global settings...</p>;
  }

  if (!globalSettings) {
    return (
      <p>Failed to load global settings. Please try refreshing the page.</p>
    );
  }

  const imageUrls = globalSettings.imageUrls || {};
  const customFonts = Array.isArray(globalSettings.customFonts)
    ? globalSettings.customFonts
    : [];
  const aiConfig = globalSettings.aiConfig || ensureAiConfig(null);

  // Group image URLs by category
  const imageCategories = {
    'Wallet Images': {
      appleWallet: imageUrls.appleWallet || '',
      googleWallet: imageUrls.googleWallet || '',
    },
    'Social Media Icons': {
      socialX: imageUrls.socialX || '',
      socialFacebook: imageUrls.socialFacebook || '',
      socialLinkedIn: imageUrls.socialLinkedIn || '',
      socialInstagram: imageUrls.socialInstagram || '',
      socialYoutube: imageUrls.socialYoutube || '',
      socialMisc: imageUrls.socialMisc || '',
    },
    'Congress App Badges': {
      congressAppApple: imageUrls.congressAppApple || '',
      congressAppGoogle: imageUrls.congressAppGoogle || '',
    },
    'Contact Icons': {
      iconEmail: imageUrls.iconEmail || '',
      iconPhone: imageUrls.iconPhone || '',
      iconWebsite: imageUrls.iconWebsite || '',
    },
    'Calendar Provider Icons': {
      calendarApple: imageUrls.calendarApple || '',
      calendarGoogle: imageUrls.calendarGoogle || '',
      calendarOutlook: imageUrls.calendarOutlook || '',
      calendarYahoo: imageUrls.calendarYahoo || '',
    },
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Default Placeholder Text
        </h3>
        <div className="p-4 bg-gray-50 border rounded-lg flex justify-between items-center">
          <p className="text-gray-600">
            Enter the placeholder editor to change the default text content seen
            by users.
          </p>
          <button
            onClick={openPlaceholderEditor}
            className="nav-button-secondary"
          >
            Open Editor
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">
          Custom Font Families
        </h3>
        <p className="text-sm text-gray-500 -mt-1">
          Fonts added here appear in the Appearance &rarr; Font Family dropdown. Make
          sure these fonts are available in your emails (system fonts or hosted via CSS).
        </p>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <FormInput
              label="Font name"
              value={newFontName}
              onChange={e => setNewFontName(e.target.value)}
              placeholder="e.g. GT Walsheim"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={handleAddCustomFont}
              className="nav-button-secondary whitespace-nowrap"
            >
              Add Font
            </button>
          </div>
        </div>
        {customFonts.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {customFonts.map(font => (
              <span
                key={`custom-font-${font}`}
                className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-sm text-slate-700"
                style={{ fontFamily: font }}
              >
                <span>{font}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveCustomFont(font)}
                  className="text-slate-500 hover:text-rose-500"
                  title="Remove font"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No custom fonts added yet. Add the exact font family name (case sensitive).
          </p>
        )}
      </div>

      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900">
          Site-wide Image URLs
        </h3>
        <p className="text-sm text-gray-500 -mt-4">
          These image URLs are used across all templates.
        </p>

        {Object.entries(imageCategories).map(([categoryName, categoryUrls]) => (
          <div key={categoryName} className="p-4 bg-white border rounded-lg">
            <h4 className="font-semibold mb-3">{categoryName}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              {Object.entries(categoryUrls).map(([key, value]) => (
                <FormInput
                  key={key}
                  label={key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())}
                  value={value || ''}
                  onChange={e => {
                    console.log('FormInput onChange:', key, e.target.value);
                    handleInputChange('imageUrls', key, e.target.value);
                  }}
                  onPaste={true}
                  placeholder={`URL for ${key}`}
                  helpText={`Enter the URL for the ${key.replace(/([A-Z])/g, ' $1').toLowerCase()} image`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900">
          AI Assistant Configuration
        </h3>
        <p className="text-sm text-gray-500 -mt-4">
          Manage translation languages and reusable prompt snippets shared with your teams.
        </p>

        <div className="p-4 bg-white border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">API credentials</h4>
          </div>
          <p className="text-xs text-gray-500">
            Credentials are stored server-side and used for AI generation. Leave fields blank to use
            environment defaults.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Provider
              </label>
              <select
                value={aiConfig.provider || 'openai'}
                onChange={e => handleAiCredentialChange('provider', e.target.value)}
                className="form-select block w-full"
              >
                <option value="openai">OpenAI</option>
              </select>
            </div>
            <FormInput
              label="Model"
              value={aiConfig.model || ''}
              onChange={e => handleAiCredentialChange('model', e.target.value)}
              placeholder="e.g. gpt-5-nano"
              helpText="Overrides the default model if provided."
            />
            <FormInput
              label="Base URL"
              value={aiConfig.baseUrl || ''}
              onChange={e => handleAiCredentialChange('baseUrl', e.target.value)}
              placeholder="https://api.openai.com/v1"
              helpText="Optional custom endpoint for your provider."
            />
            <FormInput
              label="API key"
              value={aiConfig.apiKey || ''}
              onChange={e => handleAiCredentialChange('apiKey', e.target.value)}
              placeholder="sk-..."
              type="password"
              autoComplete="new-password"
              helpText="Stored securely in global settings."
            />
          </div>
        </div>

        <div className="p-4 bg-white border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Supported languages</h4>
            <button
              type="button"
              onClick={addLanguage}
              className="text-sm font-semibold text-primary hover:text-primary/90"
            >
              Add language
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Languages appear in the visual editor for translation tasks. Provide a unique identifier, a label, and the instruction the AI should follow.
          </p>
          <div className="space-y-4">
            {aiConfig.languages.map((language, index) => (
              <div key={`ai-lang-${index}`} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-1">
                    <FormInput
                      label="Identifier"
                      value={language.id || ''}
                      onChange={e => handleLanguageChange(index, 'id', e.target.value)}
                      placeholder="e.g. de"
                    />
                    <FormInput
                      label="Label"
                      value={language.label || ''}
                      onChange={e => handleLanguageChange(index, 'label', e.target.value)}
                      placeholder="e.g. Deutsch"
                    />
                    <FormInput
                      label="Example locale"
                      value={language.locale || ''}
                      onChange={e => handleLanguageChange(index, 'locale', e.target.value)}
                      placeholder="Optional locale code"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLanguage(index)}
                    className="text-sm text-rose-500 hover:text-rose-600"
                  >
                    Remove
                  </button>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Instruction</label>
                  <textarea
                    value={language.prompt || ''}
                    onChange={e => handleLanguageChange(index, 'prompt', e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    rows={3}
                    placeholder="Describe how the AI should handle translations for this language."
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 bg-white border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Prompt snippet categories</h4>
            <button
              type="button"
              onClick={addSnippetCategory}
              className="text-sm font-semibold text-primary hover:text-primary/90"
            >
              Add category
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Snippets give editors reusable guidance such as tone adjustments or audience hints. They surface inside the AI assistant panel.
          </p>
          <div className="space-y-5">
            {aiConfig.snippetCategories.map((category, categoryIndex) => (
              <div key={`ai-cat-${categoryIndex}`} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-1">
                    <FormInput
                      label="Category ID"
                      value={category.id || ''}
                      onChange={e => handleCategoryFieldChange(categoryIndex, 'id', e.target.value)}
                      placeholder="e.g. tone"
                    />
                    <FormInput
                      label="Category label"
                      value={category.label || ''}
                      onChange={e => handleCategoryFieldChange(categoryIndex, 'label', e.target.value)}
                      placeholder="e.g. Tone"
                    />
                    <FormInput
                      label="Description"
                      value={category.description || ''}
                      onChange={e => handleCategoryFieldChange(categoryIndex, 'description', e.target.value)}
                      placeholder="Optional helper text"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSnippetCategory(categoryIndex)}
                    className="text-sm text-rose-500 hover:text-rose-600"
                  >
                    Remove
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-semibold text-gray-700">Snippets</h5>
                    <button
                      type="button"
                      onClick={() => addSnippetOption(categoryIndex)}
                      className="text-sm font-semibold text-primary hover:text-primary/90"
                    >
                      Add snippet
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(category.options || []).map((option, optionIndex) => (
                      <div
                        key={`ai-cat-${categoryIndex}-opt-${optionIndex}`}
                        className="border border-dashed border-gray-200 rounded-lg p-3 space-y-2"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <FormInput
                            label="Snippet ID"
                            value={option.id || ''}
                            onChange={e =>
                              handleSnippetChange(categoryIndex, optionIndex, 'id', e.target.value)
                            }
                            placeholder="e.g. tone-warm"
                          />
                          <FormInput
                            label="Snippet label"
                            value={option.label || ''}
                            onChange={e =>
                              handleSnippetChange(categoryIndex, optionIndex, 'label', e.target.value)
                            }
                            placeholder="Display name"
                          />
                          <FormInput
                            label="Tag (optional)"
                            value={option.tag || ''}
                            onChange={e =>
                              handleSnippetChange(categoryIndex, optionIndex, 'tag', e.target.value)
                            }
                            placeholder="e.g. Warm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Instruction</label>
                          <textarea
                            value={option.prompt || ''}
                            onChange={e =>
                              handleSnippetChange(categoryIndex, optionIndex, 'prompt', e.target.value)
                            }
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                            rows={2}
                            placeholder="Describe how the AI should adapt its writing when this snippet is chosen."
                          />
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeSnippetOption(categoryIndex, optionIndex)}
                            className="text-xs text-rose-500 hover:text-rose-600"
                          >
                            Remove snippet
                          </button>
                        </div>
                      </div>
                    ))}
                    {(!category.options || category.options.length === 0) && (
                      <p className="text-xs text-gray-500">
                        No snippets yet. Add one to provide reusable AI guidance.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {aiConfig.snippetCategories.length === 0 && (
              <p className="text-xs text-gray-500">
                No categories configured. Add one to start organising AI snippets.
              </p>
            )}
          </div>
        </div>

        <div className="p-4 bg-white border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Text template quick actions</h4>
            <button
              type="button"
              onClick={addQuickActionGroup}
              className="text-sm font-semibold text-primary hover:text-primary/90"
            >
              Add group
            </button>
          </div>
          <p className="text-xs text-gray-500">
            These actions appear in the floating AI menu inside the text template editor.
          </p>
          <div className="space-y-5">
            {(aiConfig.textTemplateQuickActions || []).map((group, groupIndex) => (
              <div
                key={`ai-quick-group-${groupIndex}`}
                className="border border-gray-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
                    <FormInput
                      label="Group label"
                      value={group.label || ''}
                      onChange={e =>
                        handleQuickActionGroupChange(groupIndex, 'label', e.target.value)
                      }
                      placeholder="e.g. Rewrite"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeQuickActionGroup(groupIndex)}
                    className="text-sm text-rose-500 hover:text-rose-600"
                  >
                    Remove
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-semibold text-gray-700">Actions</h5>
                    <button
                      type="button"
                      onClick={() => addQuickAction(groupIndex)}
                      className="text-sm font-semibold text-primary hover:text-primary/90"
                    >
                      Add action
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(group.actions || []).map((action, actionIndex) => (
                      <div
                        key={`ai-quick-${groupIndex}-${actionIndex}`}
                        className="border border-dashed border-gray-200 rounded-lg p-3 space-y-2"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <FormInput
                            label="Action label"
                            value={action.label || ''}
                            onChange={e =>
                              handleQuickActionChange(
                                groupIndex,
                                actionIndex,
                                'label',
                                e.target.value
                              )
                            }
                            placeholder="Display name"
                          />
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                              Command
                            </label>
                            <select
                              value={action.command || 'improve'}
                              onChange={e =>
                                handleQuickActionChange(
                                  groupIndex,
                                  actionIndex,
                                  'command',
                                  e.target.value
                                )
                              }
                              className="form-select block w-full"
                            >
                              {QUICK_ACTION_COMMANDS.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <FormInput
                            label="Prompt (optional)"
                            value={action.prompt || ''}
                            onChange={e =>
                              handleQuickActionChange(
                                groupIndex,
                                actionIndex,
                                'prompt',
                                e.target.value
                              )
                            }
                            placeholder="Extra instruction for the AI"
                          />
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeQuickAction(groupIndex, actionIndex)}
                            className="text-xs text-rose-500 hover:text-rose-600"
                          >
                            Remove action
                          </button>
                        </div>
                      </div>
                    ))}
                    {(!group.actions || group.actions.length === 0) && (
                      <p className="text-xs text-gray-500">
                        No actions yet. Add one to populate this group.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(!aiConfig.textTemplateQuickActions ||
              aiConfig.textTemplateQuickActions.length === 0) && (
              <p className="text-xs text-gray-500">
                No quick action groups configured yet.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="pt-4 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || loading}
          className="nav-button-primary"
        >
          {saving ? 'Saving...' : 'Save Site Configuration'}
        </button>
      </div>
    </div>
  );
};

export default GlobalSettings;
